/**
 * Pure geometry helpers for `ResizableSplit`'s pointer/keyboard-driven resize. No React import here
 * on purpose: this is plain, dependency-free math so it can be unit tested directly (see
 * `ResizableSplit.test.tsx`'s `clampPct` describe block) and shared by both the pointer-move handler
 * (needs a single `pct` for a given `clientX`) and the keyboard / `aria-valuemin`/`aria-valuemax`
 * code (needs the bounds alone, independent of any pointer position) -- so both agree on the exact
 * same numbers.
 *
 * COORDINATE SYSTEM: `pct` is a percentage of the FULL container width `W = rect.width` -- matching
 * EXACTLY what `ResizableSplit.tsx` renders: `gridTemplateColumns: calc(${pct}% - ${gutterPx / 2}px) ${gutterPx}px
 * calc(${100 - pct}% - ${gutterPx / 2}px)`. (There is also a second,
 * INCOMPATIBLE `usableWidth = rect.width - gutterPx` model -- the two are mutually exclusive and agree only at `pct = 50`. The grid
 * template is what actually renders, so it is authoritative; the previous implementation followed
 * that other model instead and silently rendered panes up to ~1.1px narrower than their configured pixel
 * minimums.) Under this full-width model, the rendered LEFT pane's pixel width at a given `pct` is
 * `pct/100 * W - gutterPx/2`, and the RIGHT pane's is `(100-pct)/100 * W - gutterPx/2`. Every bound
 * derived below is chosen so plugging it back into those two formulas reproduces `leftMinPx` /
 * `rightMinPx` EXACTLY, not approximately.
 */

export interface ClampBounds {
  /** Minimum left-pane percentage that still leaves `rightMinPx` for the right pane. */
  min: number;
  /** Maximum left-pane percentage that still leaves `leftMinPx` for the left pane. */
  max: number;
}

/**
 * Floating-point-safe tolerance for the `min`/`max` "doesn't fit" comparison in `getClampBounds`,
 * AND for the near-exact-fit CANONICALIZATION right below it. At EXACT fit
 * (`leftMinPx + rightMinPx + gutterPx === width`), `min` and `max` are
 * mathematically the identical value, but they are computed via two DIFFERENT arithmetic paths
 * (`min` directly from `leftMinPx`; `max` as `100 - <rightMinPx-derived percentage>`), so in
 * practice they can land a few float64 ULPs apart -- empirically confirmed ~7e-15 apart for
 * leftMinPx=480/rightMinPx=360/gutterPx=8/width=848 (with `min` the LARGER of the two at that exact
 * combination -- i.e. genuinely inverted, not just "not bit-identical") -- even though they
 * represent the SAME split point. A bare `min > max` is still true across that ~7e-15 gap, so it
 * would still misfire into the gutter-blind proportional fallback exactly like the pre-fix
 * `min >= max` did. This epsilon is many orders of magnitude above observed float64 noise at this
 * magnitude, and many orders below any visually meaningful percentage difference (1e-9 of a percent
 * is ~1e-11px on a 1000px-wide container) -- real `getBoundingClientRect()` widths are essentially
 * never exact integers either, so this can't be sidestepped by "just use integer-friendly test
 * fixtures".
 */
const CLAMP_BOUNDS_EPSILON = 1e-9;

/**
 * Clamps a percentage into the valid `[0,100]` closed interval. Applied to
 * every value `getClampBounds` returns so that even a defensively-normalized-but-still-extreme input
 * -- e.g. `gutterPx > 2 * width`, which drives the non-fit fallback's raw pct above 100 -- can never
 * emit an out-of-range `aria-valuemin`/`aria-valuemax` or a `calc()` track positioned outside the
 * container. Monotonic (non-decreasing), so it can never INVERT an already-ordered `min <= max` pair,
 * and it maps two equal inputs to two equal outputs -- which is what lets the near-exact-fit
 * canonicalization below stay intact (a single canonical value in still yields that same single value
 * out, just bounded).
 */
function clampToPercent(pct: number): number {
  return Math.min(100, Math.max(0, pct));
}

/**
 * Computes the `{min,max}` left-pane-percentage bounds for a container of the given `rect`, given
 * both panes' pixel minimums and the gutter width.
 *
 * `pct` is a percentage of the FULL `rect.width` (see the file header), so the left pane's
 * rendered width at a given `pct` is `pct/100 * rect.width - gutterPx/2`. Setting that equal to
 * `leftMinPx` and solving for `pct` gives `min = (leftMinPx + gutterPx/2) / rect.width * 100`; the
 * symmetric derivation for the right pane (`(100-pct)/100 * rect.width - gutterPx/2 = rightMinPx`)
 * gives `max = 100 - (rightMinPx + gutterPx/2) / rect.width * 100`.
 *
 * When the two pixel minimums (plus the gutter) don't fit in the container at all, there is no
 * valid `[min,max]` range left to clamp into: `min` and `max` collapse to the SAME single split
 * point -- the left pane's share of the USABLE (gutter-subtracted) width, converted back into the
 * full-width percentage the renderer expects -- rather than NaN or an inverted
 * range, so every caller (`clampPct` below, and `ResizableSplit`'s `aria-valuemin`/`now`/`max` +
 * keyboard Home/End) sees one single, self-consistent fallback value instead of each needing its own
 * degenerate-case handling. A non-positive `rect.width` (e.g. an unmeasured/detached container)
 * degrades to the same shape via a fixed `{min: 50, max: 50}` so callers never see `NaN`/`Infinity`;
 * non-finite or negative minima/gutter are likewise normalized to 0 up front for the same reason.
 * At NEAR-exact fit (mins don't clear the "genuinely doesn't fit" epsilon but `min`/`max` are not
 * bit-identical either), the same collapse-to-a-single-value guarantee applies for the same reason:
 * see CLAMP_BOUNDS_EPSILON's doc comment and the canonicalization branch
 * below.
 */
export function getClampBounds(
  rect: DOMRect,
  leftMinPx: number,
  rightMinPx: number,
  gutterPx: number
): ClampBounds {
  const width = rect.width;

  if (!(width > 0)) {
    return { min: 50, max: 50 };
  }

  // Defensively normalize the pixel inputs. Only `width` was guarded before,
  // yet these fns are EXPORTED and reused, and `gutterPx` in particular has NO config validator (the
  // config schema validates `minPx`, not the gutter). A non-finite (NaN/Infinity) or negative
  // `leftMinPx`/`rightMinPx`/`gutterPx` would otherwise flow straight into the arithmetic below and
  // back out as a NaN/Infinity bound or a negative rendered track. Treat any such value as 0 -- the
  // safe identity for both a pane "minimum" and a "gutter" -- and use ONLY the normalized values
  // (`g`/`lMin`/`rMin`) from here down.
  const g = Number.isFinite(gutterPx) && gutterPx > 0 ? gutterPx : 0;
  const lMin = Number.isFinite(leftMinPx) && leftMinPx > 0 ? leftMinPx : 0;
  const rMin = Number.isFinite(rightMinPx) && rightMinPx > 0 ? rightMinPx : 0;
  const halfGutterPx = g / 2;

  const leftMinPct = ((lMin + halfGutterPx) / width) * 100;
  const rightMinPct = ((rMin + halfGutterPx) / width) * 100;
  const min = leftMinPct;
  const max = 100 - rightMinPct;
  const diff = min - max;

  if (diff > CLAMP_BOUNDS_EPSILON) {
    // Mins (plus gutter) genuinely don't fit -- collapse to a finite, non-inverted proportional
    // split instead of an inverted [min,max] range. Epsilon-tolerant, not a bare `min > max`:
    // at EXACT fit (leftMinPx + rightMinPx + gutterPx === width), `min` and `max`
    // are mathematically equal -- both reduce to (leftMinPx + gutterPx/2) / width * 100 -- and that
    // shared value IS the single valid split point (plugging it back into the rendered-width
    // formulas reproduces leftMinPx/rightMinPx exactly), not an impossible/degenerate case. The old
    // `min >= max` treated min===max the same as a genuine inversion and substituted the
    // gutter-BLIND proportional split `leftMinPx / (leftMinPx + rightMinPx) * 100` instead, which --
    // once run through the actual `calc(pct% - gutterPx/2px)` render formula -- shortchanges
    // whichever pane the gutter's other half was quietly dropped from, below its own configured
    // minimum. A bare `min > max` (no epsilon) does not actually fix this in practice: `min`/`max`
    // are computed via two different float64 paths (see CLAMP_BOUNDS_EPSILON's doc comment) and can
    // land a few ULPs apart at true exact fit, so `min > max` alone can still misfire into this
    // same fallback -- see CLAMP_BOUNDS_EPSILON.
    // Allocate the USABLE (gutter-subtracted) width, NOT the full
    // 100%. Split `usable` proportionally between the panes to get the left pane's target pixel width,
    // then convert THAT back into the full-width percentage the renderer expects. The renderer draws
    // the left track as `pct/100 * width - gutterPx/2`, so to make that track equal `leftTrackPx` we
    // need `pct = (leftTrackPx + gutterPx/2) / width * 100`. The previous fallback distributed the
    // full 100% proportionally and ignored the gutter entirely: e.g. width=320, leftMinPx=0,
    // rightMinPx=360, gutterPx=8 returned {0,0}, which the renderer drew as a `calc(0% - 4px)` = -4px
    // left track while the right track (316px) + gutter (8px) overshot the 320px container by 4px --
    // a negative rendered track AND horizontal overflow. Allocating the usable width keeps BOTH
    // rendered tracks >= 0 and their sum + gutter == width. `usable` is floored at 0 so a degenerate
    // `gutterPx >= width` yields usable=0 (both target tracks 0); `clampToPercent` then keeps the
    // resulting pct within [0,100] even for the further-degenerate `gutterPx > 2 * width`.
    const usable = Math.max(width - g, 0);
    const totalMinPx = lMin + rMin;
    const leftTrackPx =
      totalMinPx > 0 ? (lMin / totalMinPx) * usable : usable / 2;
    const pct = clampToPercent(((leftTrackPx + halfGutterPx) / width) * 100);
    return { min: pct, max: pct };
  }

  if (Math.abs(diff) <= CLAMP_BOUNDS_EPSILON) {
    // Near-exact fit: the branch above correctly does NOT treat
    // this as "mins don't fit" (diff is not GREATER than the epsilon), but simply returning the raw
    // computed {min, max} here is not safe either -- `min` and `max` can still land a few float64
    // ULPs apart (see CLAMP_BOUNDS_EPSILON's doc comment; empirically `min` was the LARGER of the
    // two at leftMinPx=480/rightMinPx=360/gutterPx=8/width=848), which is a genuine, if tiny,
    // INVERSION: `aria-valuemin` would render a hair ABOVE `aria-valuemax`, and the mount-time clamp
    // (`Math.min(Math.max(prev, min), max)`) would resolve to `max` rather than `min` for any
    // out-of-range `prev`, silently disagreeing with a keyboard Home press (`setPct(bounds.min)`) or
    // a pointer dragged to the same physical edge -- two callers of the SAME bounds landing on two
    // different pct values. Canonicalize to a single value so `min <= max` ALWAYS holds in the
    // returned range, not just "usually holds within epsilon": every caller (this function's own
    // `clampPct` below, and ResizableSplit's aria-valuemin/now/max + keyboard Home/End) then sees
    // one exact, self-consistent bound, the same guarantee the "mins don't fit" branch above already
    // gives the genuinely-inverted case. `min` (the LEFT-pane-derived value) is used as the single
    // canonical bound -- an arbitrary but consistent choice between the two near-identical inputs;
    // `clampPct`'s own `bounds.min >= bounds.max` fallback already favors `min` in this situation
    // for the same reason. Routed through clampToPercent so the single value is ALSO guaranteed
    // within [0,100]; since both fields receive the SAME clamped value, this canonicalization's
    // `min <= max` guarantee is preserved exactly.
    const canonical = clampToPercent(min);
    return { min: canonical, max: canonical };
  }

  // Mins fit with a live range to spare (`min < max`): both are already within [0,100] by
  // construction, but route them through clampToPercent anyway so the finite/[0,100] guarantee
  // holds unconditionally. clampToPercent is monotonic, so it can never invert this already-ordered
  // pair.
  return { min: clampToPercent(min), max: clampToPercent(max) };
}

/**
 * Converts a pointer's `clientX` into a clamped left-pane percentage. The raw
 * percentage is measured directly against the FULL container width from its left edge -- no
 * gutter offset subtracted -- because at that raw (unclamped) value the rendered gutter track
 * (`gutterPx` wide, positioned right after the left track) ends up CENTERED under `clientX`, which
 * is what "the separator follows the pointer" means visually: left track ends at
 * `pct/100*W - gutterPx/2 = clientX - gutterPx/2`, gutter spans `gutterPx` from there, so it runs
 * from `clientX - gutterPx/2` to `clientX + gutterPx/2`.
 *
 * The raw value is then clamped into the bounds from `getClampBounds` -- shared with the keyboard
 * handler and the rendered `aria-valuemin`/`max` so pointer and keyboard interaction always agree
 * on the exact same bounds.
 *
 * Falls back to `getClampBounds`'s fixed single-value split (the gutter-aware usable-width split, or
 * `{50,50}` for an unmeasured container) when the pane minimums don't fit the container at all
 * (`min >= max`), instead of computing a raw value that would be meaningless for a zero/negative width.
 */
export function clampPct(
  clientX: number,
  rect: DOMRect,
  leftMinPx: number,
  rightMinPx: number,
  gutterPx: number
): number {
  const bounds = getClampBounds(rect, leftMinPx, rightMinPx, gutterPx);
  if (bounds.min >= bounds.max) {
    // Mins don't fit (or the container is unmeasured) -- getClampBounds already collapsed
    // min/max to the same value; there is no live range left to place a pointer-driven position
    // within.
    return bounds.min;
  }

  const raw = ((clientX - rect.left) / rect.width) * 100;
  return Math.min(Math.max(raw, bounds.min), bounds.max);
}
